import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useVoiceSettings } from '@/hooks/useVoiceSettings';
import { walletService, staffService, taskService, paidLeaveService, expenseService } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckSquare,
  User,
  LogOut,
  Menu,
  X,
  FileText,
  Sun,
  Moon,
  Calendar,
  Wallet,
  CalendarOff,
  MapPin,
  IndianRupee,
  MousePointer
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface StaffLayoutProps {
  children: ReactNode;
}

const baseNavItems = [
  { icon: CheckSquare, label: 'My Tasks', path: '/staff' },
  { icon: Calendar, label: 'Attendance', path: '/staff/attendance' },
  { icon: IndianRupee, label: 'Expenses', path: '/staff/expenses' },
  { icon: Wallet, label: 'Wallet', path: '/staff/wallet' },
  { icon: CalendarOff, label: 'Paid Leave', path: '/staff/paid-leaves' },
  { icon: User, label: 'Profile', path: '/staff/profile' },
];

const StaffLayout = ({ children }: StaffLayoutProps) => {
  const { session, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { playSound } = useVoiceSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [isSpecial, setIsSpecial] = useState(false);
  const [cursorActive, setCursorActive] = useState(() => localStorage.getItem('customCursorEnabled') !== 'false');

  const toggleCursor = () => {
    const newValue = !cursorActive;
    setCursorActive(newValue);
    localStorage.setItem('customCursorEnabled', String(newValue));
    window.dispatchEvent(new Event('cursor-setting-changed'));
  };

  const [pendingTasks, setPendingTasks] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [pendingExpenses, setPendingExpenses] = useState(0);

  const loadStaffNotificationCounts = async () => {
    if (!session?.userId) return;
    try {
      const [tasks, leaves, expenses] = await Promise.all([
        taskService.getByStaffId(session.userId),
        paidLeaveService.getAll({ staffId: session.userId }),
        expenseService.getAll({ staffId: session.userId })
      ]);
      setPendingTasks(tasks.filter(t => t.status !== 'Completed').length);
      setPendingLeaves(leaves.filter(l => l.status === 'pending').length);
      setPendingExpenses(expenses.filter(e => e.status === 'pending').length);
    } catch (e) {
      console.error('Failed to load staff notification counts', e);
    }
  };

  useEffect(() => {
    if (session?.userId && session.role === 'staff') {
      walletService.getBalance(session.userId)
        .then(w => setWalletBalance(w.balance))
        .catch(() => setWalletBalance(0));

      staffService.getById(session.userId)
        .then(s => setIsSpecial(!!s?.isSpecial))
        .catch(() => setIsSpecial(false));
      
      loadStaffNotificationCounts();
      const interval = setInterval(loadStaffNotificationCounts, 10000);
      return () => clearInterval(interval);
    }
  }, [session?.userId]);


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = isSpecial
    ? [...baseNavItems.slice(0, 4), { icon: MapPin, label: 'Travel Allowance', path: '/staff/travel-allowance' }, ...baseNavItems.slice(4)]
    : baseNavItems;

  return (
    <div className="min-h-screen flex w-full bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 antialiased">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-r border-slate-200/50 dark:border-slate-800/50 flex flex-col transition-transform duration-300 ease-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-slate-100 dark:border-slate-800/30">
          <Link to="/staff" className="flex items-center gap-2 hover:opacity-95 transition-opacity">
            <img src="/logo.png" alt="Royal 300" className="h-10 w-auto object-contain" />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto scrollbar-none">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const countMap: Record<string, number> = {
              'My Tasks': pendingTasks,
              'Paid Leave': pendingLeaves,
              'Wallet': pendingExpenses,
            };
            const count = countMap[item.label] || 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "relative flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 group overflow-hidden",
                  isActive
                    ? "text-white"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100/50 dark:hover:bg-slate-800/50"
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeStaffNav"
                    className="absolute inset-0 bg-success shadow-lg shadow-success/20 -z-10"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <item.icon className={cn("w-5 h-5 transition-transform duration-200", isActive ? "scale-110" : "group-hover:scale-105")} />
                <span className="relative z-10">{item.label}</span>
                {count > 0 && (
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 min-w-5 h-5 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-black px-1.5 shadow-md shadow-rose-500/20 animate-pulse">
                    {count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-800/30">
          <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-xl bg-slate-100/50 dark:bg-slate-800/30">
            <div className="w-9 h-9 rounded-full bg-success/10 flex items-center justify-center border border-success/20">
              <span className="text-success font-bold text-sm">
                {session?.name?.charAt(0) || 'S'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate leading-none">{session?.name}</p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium mt-1 truncate">Staff Member</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200/40 dark:border-amber-800/30">
            <Wallet className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-xs font-bold text-amber-700 dark:text-amber-400">Wallet: ₹{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start rounded-xl text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen w-full lg:pl-64">
        {/* Top bar */}
        <header className="h-20 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-800/50 flex items-center justify-between px-6 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Menu className="w-5.5 h-5.5" />
            </button>
            <h1 className="text-xl font-bold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
              {navItems.find(item => item.path === location.pathname)?.label || 'My Tasks'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 text-xs font-semibold border border-amber-200/60 dark:border-amber-800/30 shadow-sm">
              <Wallet className="w-3.5 h-3.5" />
              <span>₹{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <button
              onClick={toggleCursor}
              className={cn(
                "p-2.5 rounded-xl transition-all active:scale-95 flex items-center justify-center shrink-0 border shadow-sm",
                cursorActive 
                  ? "text-indigo-600 dark:text-indigo-400 border-indigo-200/50 dark:border-indigo-800/40 bg-indigo-50/50 dark:bg-indigo-950/20"
                  : "text-slate-500 dark:text-slate-400 border-slate-200/40 dark:border-slate-800/40 bg-white/50 dark:bg-slate-900/30"
              )}
              aria-label="Toggle Custom Cursor"
              title="Toggle Custom Cursor"
            >
              <MousePointer className="w-4.5 h-4.5" />
            </button>
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-855 transition-all active:scale-95 flex items-center justify-center shrink-0 border border-slate-200/40 dark:border-slate-800/40 bg-white/50 dark:bg-slate-900/30 shadow-sm"
              aria-label="Toggle Theme"
            >
              {theme === 'dark' ? <Sun className="w-4.5 h-4.5 text-amber-500" /> : <Moon className="w-4.5 h-4.5 text-slate-500" />}
            </button>
            <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/5 dark:bg-success-950/30 text-success dark:text-success-400 text-xs font-semibold border border-success/10 dark:border-success-900/20">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Staff Portal
            </div>
          </div>
        </header>

        {/* Page content with smooth route change transition */}
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-8 lg:p-8 overflow-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="w-full h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Mobile Bottom Navigation Bar */}
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-slate-200/50 dark:border-slate-800/60 pb-safe-bottom lg:hidden h-16 flex items-center justify-around px-2 shadow-lg shadow-slate-950/5">
          {/* Tasks */}
          <Link
            to="/staff"
            className={cn(
              "flex flex-col items-center gap-1 text-[10px] font-bold transition-all duration-200 shrink-0 relative",
              location.pathname === '/staff' ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400"
            )}
          >
            <div className="relative">
              <CheckSquare className="w-5.5 h-5.5" />
              {pendingTasks > 0 && (
                <span className="absolute -top-1 -right-2 min-w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-white text-[8px] font-black px-1 shadow-md">
                  {pendingTasks}
                </span>
              )}
            </div>
            <span>Tasks</span>
          </Link>

          {/* Reports */}
          <Link
            to="/staff/reports"
            className={cn(
              "flex flex-col items-center gap-1 text-[10px] font-bold transition-all duration-200 shrink-0",
              location.pathname === '/staff/reports' ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400"
            )}
          >
            <FileText className="w-5.5 h-5.5" />
            <span>Reports</span>
          </Link>

          {/* Attendance (Middle Floating Button) */}
          <Link
            to="/staff/attendance"
            className="flex flex-col items-center -translate-y-4 relative z-50 shrink-0"
          >
            <div className={cn(
              "w-12.5 h-12.5 rounded-full flex items-center justify-center text-white shadow-xl border-4 border-slate-50 dark:border-slate-950 transition-all duration-200 active:scale-90 relative",
              location.pathname === '/staff/attendance'
                ? "bg-gradient-to-br from-indigo-500 to-violet-600 shadow-indigo-500/35 scale-105"
                : "bg-slate-700 dark:bg-slate-800 shadow-slate-950/20"
            )}>
              <Calendar className="w-5.5 h-5.5" />
            </div>
            <span className={cn(
              "text-[10px] font-bold mt-1 transition-colors duration-200",
              location.pathname === '/staff/attendance' ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500"
            )}>Attendance</span>
          </Link>

          {/* Wallet */}
          <Link
            to="/staff/wallet"
            className={cn(
              "flex flex-col items-center gap-1 text-[10px] font-bold transition-all duration-200 shrink-0 relative",
              location.pathname === '/staff/wallet' ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400"
            )}
          >
            <div className="relative">
              <Wallet className="w-5.5 h-5.5" />
              {pendingExpenses > 0 && (
                <span className="absolute -top-1 -right-2 min-w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-white text-[8px] font-black px-1 shadow-md">
                  {pendingExpenses}
                </span>
              )}
            </div>
            <span>Wallet</span>
          </Link>

          {/* Profile */}
          <Link
            to="/staff/profile"
            className={cn(
              "flex flex-col items-center gap-1 text-[10px] font-bold transition-all duration-200 shrink-0",
              location.pathname === '/staff/profile' ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400"
            )}
          >
            <User className="w-5.5 h-5.5" />
            <span>Profile</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StaffLayout;
